// Every Director task write goes through the shared write core (`use-writes.ts`): the version the
// user last saw, a request id reused on an identical retry, one request per key in flight, and the
// four failure outcomes a screen can explain.
import { useService } from '@toeverything/infra';
import { useCallback } from 'react';

import {
  type DirectorBulkUpdateInput,
  type DirectorClearAction,
  type DirectorInboxItem,
  type DirectorTask,
  type DirectorTaskActionInput,
  type DirectorTaskEvent,
  ErpService,
} from '../../../../../modules/erp';
import { requestErpRefresh } from '../../../../../modules/erp/refresh';
import { useWrites, type WriteError } from './use-writes';

type DistributiveOmit<T, K extends PropertyKey> = T extends unknown
  ? Omit<T, K>
  : never;

/** A task action as a screen states it; the hook adds `requestId` and `expectedVersion`. */
export type DirectorTaskActionBody = DistributiveOmit<
  DirectorTaskActionInput,
  'requestId' | 'expectedVersion'
>;

/** One action: the server's task and the event the action wrote. */
type ActSuccess = { ok: true; task: DirectorTask; event: DirectorTaskEvent };
/** The bulk update: the server's tasks and their events, in the order sent (parallel arrays). */
type BulkSuccess = {
  ok: true;
  tasks: DirectorTask[];
  events: DirectorTaskEvent[];
};
type ClearSuccess = { ok: true };
export type ActResult = ActSuccess | WriteError;
export type BulkResult = BulkSuccess | WriteError;
/** An action or a clear, as the row screens settle them. */
export type WriteResult = ActResult | ClearSuccess;

export const taskKey = (taskId: string, action: string) =>
  `task:${taskId}:${action}`;
export const clearKey = (itemId: string, action: string) =>
  `inbox:${itemId}:${action}`;
export const BULK_KEY = 'bulk';
export const replyKey = (taskId: string) => `reply:${taskId}`;

export function useTaskWrites() {
  const erp = useService(ErpService);
  const { write, renew, pending } = useWrites<
    ActSuccess | BulkSuccess | ClearSuccess
  >();

  const act = useCallback(
    (task: DirectorTask, action: DirectorTaskActionBody) => {
      const body = { ...action, expectedVersion: task.version };
      return write(
        taskKey(task.id, action.action),
        body,
        async (requestId): Promise<ActSuccess> => {
          const res = await erp.directorTaskAction(task.id, {
            requestId,
            ...body,
          } as DirectorTaskActionInput);
          return { ok: true, task: res.task, event: res.event };
        }
      );
    },
    [erp, write]
  );

  /** A quick reply: an ordinary comment sent without a version (a comment never changes the task,
   *  so the reply needs no fresh read). A retry of the same text after a failure reuses its request
   *  id; a 409 (that id already spent on another request) is retried once with a new id. On
   *  success every mounted reader revalidates, so an open task detail shows the reply at once. */
  const reply = useCallback(
    async (taskId: string, text: string): Promise<ActResult> => {
      const key = replyKey(taskId);
      const body = { action: 'comment', body: text } as const;
      const send = async (requestId: string): Promise<ActSuccess> => {
        const res = await erp.directorTaskAction(taskId, {
          requestId,
          ...body,
        });
        return { ok: true, task: res.task, event: res.event };
      };
      let result = await write(key, body, send);
      if (!result.ok && result.kind === 'conflict') {
        renew(key);
        result = await write(key, body, send);
      }
      if (result.ok) requestErpRefresh();
      return result;
    },
    [erp, renew, write]
  );

  const clear = useCallback(
    /** Any clearable item — an inbox item or a delivery. */
    (
      item: Pick<DirectorInboxItem, 'id' | 'version'>,
      action: DirectorClearAction
    ) => {
      const body = { action, expectedVersion: item.version };
      return write(
        clearKey(item.id, action),
        body,
        async (requestId): Promise<ClearSuccess> => {
          await erp.directorClearInbox(item.id, { requestId, ...body });
          return { ok: true };
        }
      );
    },
    [erp, write]
  );

  const bulk = useCallback(
    (items: DirectorBulkUpdateInput['items']) =>
      write(BULK_KEY, { items }, async (requestId): Promise<BulkSuccess> => {
        const res = await erp.directorBulkUpdates({ requestId, items });
        return {
          ok: true,
          tasks: res.items.map(i => i.task),
          events: res.items.map(i => i.event),
        };
      }),
    [erp, write]
  );

  return { act, reply, clear, bulk, pending };
}
